import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import {
  ClipboardList,
  History,
  BarChart3
} from 'lucide-react';
import PurchaseApprovalBadge from './PurchaseApprovalBadge';
import PurchaseApprovalActions from './PurchaseApprovalActions';
import PurchaseApprovalHistory from './PurchaseApprovalHistory';

const PurchaseApprovalPanel = ({
  order,
  userRole = 'user',
  onApprovalChange,
  showAnalytics = false
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [approvalStats, setApprovalStats] = useState(null);

  useEffect(() => {
    if (showAnalytics && userRole === 'admin') {
      loadApprovalStats();
    }
  }, [showAnalytics, userRole]);

  const loadApprovalStats = async () => {
    setApprovalStats({
      pending: 5,
      approved: 23,
      rejected: 2,
      avgApprovalTime: '2.3天'
    });
  };

  const handleApprovalChange = (updatedOrder) => {
    if (onApprovalChange) {
      onApprovalChange(updatedOrder);
    }
  };

  const getApprovalMessage = () => {
    switch (order?.approval_status?.toLowerCase()) {
      case 'pending':
        return {
          type: 'warning',
          message: '此采购单正在等待审批。请联系管理员或等待审批完成。'
        };
      case 'approved':
        return {
          type: 'success',
          message: '此采购单已获得批准，可以进行下一步操作。'
        };
      case 'rejected':
        return {
          type: 'error',
          message: '此采购单已被拒绝。请查看拒绝原因并进行相应修改。'
        };
      default:
        return {
          type: 'info',
          message: '此采购单尚未提交审批。'
        };
    }
  };

  const approvalMessage = getApprovalMessage();

  if (!order) {
    return (
      <Alert>
        <AlertDescription>无法加载审批信息</AlertDescription>
      </Alert>
    );
  }

  const tabs = [
    { icon: ClipboardList, label: '审批操作' },
    { icon: History, label: '历史记录' },
    ...(showAnalytics && userRole === 'admin' ? [{ icon: BarChart3, label: '统计分析', badge: approvalStats?.pending }] : [])
  ];

  const getAlertVariant = (type) => {
    if (type === 'error') return 'destructive';
    return 'default';
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">审批管理</h2>
        <PurchaseApprovalBadge
          status={order.approval_status}
          size="medium"
        />
      </div>

      <Separator className="mb-6" />

      <Alert
        variant={getAlertVariant(approvalMessage.type)}
        className={`mb-6 ${
          approvalMessage.type === 'warning' ? 'border-warning/20 bg-warning/10' :
          approvalMessage.type === 'success' ? 'border-success/20 bg-success/10' : ''
        }`}
      >
        <AlertDescription>{approvalMessage.message}</AlertDescription>
      </Alert>

      {/* Tab Navigation */}
      <div className="flex border-b mb-6">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === index
                ? 'border-blue-500 text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.label}</span>
            {tab.badge && (
              <Badge variant="secondary" className="ml-1 bg-warning/20 text-warning">
                {tab.badge}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {/* Approval Actions Tab */}
      {activeTab === 0 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium mb-3">当前状态</h3>
            <div className="flex gap-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">订单状态</p>
                <PurchaseApprovalBadge
                  status={order.status}
                  tooltip={false}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">审批状态</p>
                <PurchaseApprovalBadge
                  status={order.approval_status}
                  tooltip={false}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-3">可执行操作</h3>
            <PurchaseApprovalActions
              order={order}
              userRole={userRole}
              onApprovalChange={handleApprovalChange}
              variant="contained"
              size="medium"
            />
          </div>

          {(order.rejection_reason || order.comment) && (
            <>
              <Separator />
              {order.rejection_reason && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <p className="font-medium mb-1">拒绝原因:</p>
                    <p>{order.rejection_reason}</p>
                  </AlertDescription>
                </Alert>
              )}
              {order.comment && (
                <div>
                  <h3 className="text-sm font-medium mb-2">订单备注:</h3>
                  <p className="text-sm text-muted-foreground">{order.comment}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Approval History Tab */}
      {activeTab === 1 && (
        <PurchaseApprovalHistory order={order} />
      )}

      {/* Analytics Tab (Admin only) */}
      {activeTab === 2 && showAnalytics && userRole === 'admin' && (
        <div>
          <h3 className="text-sm font-medium mb-4">审批统计概览</h3>
          {approvalStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-warning/20 rounded-lg">
                <p className="text-3xl font-bold text-warning">
                  {approvalStats.pending}
                </p>
                <p className="text-sm text-warning">待审批</p>
              </div>
              <div className="text-center p-4 bg-success/20 rounded-lg">
                <p className="text-3xl font-bold text-success">
                  {approvalStats.approved}
                </p>
                <p className="text-sm text-success">已批准</p>
              </div>
              <div className="text-center p-4 bg-destructive/20 rounded-lg">
                <p className="text-3xl font-bold text-destructive">
                  {approvalStats.rejected}
                </p>
                <p className="text-sm text-destructive">已拒绝</p>
              </div>
              <div className="text-center p-4 bg-primary/20 rounded-lg">
                <p className="text-3xl font-bold text-primary">
                  {approvalStats.avgApprovalTime}
                </p>
                <p className="text-sm text-primary">平均审批时间</p>
              </div>
            </div>
          ) : (
            <Alert>
              <AlertDescription>正在加载统计数据...</AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default PurchaseApprovalPanel;
